/**
 * PDFViewer — renders a PDF page-by-page as canvases in a scrollable
 * column. We own the scroll container, so we can detect "user has scrolled
 * to the end" reliably (something an `<iframe src=*.pdf>` can never give us
 * because Chrome's built-in PDF plugin doesn't bubble scroll events).
 *
 * pdfjs-dist is dynamically imported on first use so its ~600 KB doesn't
 * land in the main learner-home bundle.
 *
 * Props
 *   src        – string URL of the PDF (must be same-origin to avoid CORS)
 *   onReadEnd  – called once when the user scrolls past 95 % of the last page
 *   onProgress – (optional) called with 0-100 every scroll tick
 *   ariaLabel  – screen-reader label for the scroll region
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { getToken } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Build a fully-qualified URL for the PDF. If a caller passes a relative
 * `/api/...` path we prepend `REACT_APP_BACKEND_URL` so it routes through
 * the Emergent ingress in production. Absolute URLs pass through unchanged.
 */
function resolveSrc(src) {
  if (!src) return src;
  if (/^https?:\/\//i.test(src)) return src;
  if (src.startsWith("/")) return `${BACKEND}${src}`;
  return src;
}

/**
 * Fetch a PDF as an ArrayBuffer using the same JWT the rest of the app
 * uses. We can't rely on pdfjs `getDocument({ url })` for protected
 * endpoints because there's no way to attach Authorization headers
 * cleanly across browsers/workers — feeding pdfjs raw bytes works
 * everywhere.
 */
async function fetchPdfBytes(src) {
  const token = getToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const res = await fetch(resolveSrc(src), {
    method: "GET",
    headers,
    credentials: "omit",
  });
  if (!res.ok) {
    const errBody = await res.text().catch(() => "");
    throw new Error(`PDF fetch failed (${res.status}): ${errBody.slice(0, 200) || res.statusText}`);
  }
  return res.arrayBuffer();
}

export function PDFViewer({ src, onReadEnd, onProgress, ariaLabel = "PDF document" }) {
  const containerRef = useRef(null);
  const pagesRef = useRef([]);  // holds canvas DOM refs
  const onReadEndCalledRef = useRef(false);
  const loadingRef = useRef(true); // consulted by handleScroll (read-end must wait for full render)
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ─── Load + render every page once ────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function render() {
      try {
        loadingRef.current = true;
        setLoading(true);
        setError(null);
        const pdfjs = await import("pdfjs-dist");
        // pdfjs v4 ships a separate worker file. CRA/webpack doesn't support
        // Vite-style `?url` imports, so we point at the cdnjs-hosted worker
        // for the exact pdfjs version we bundled. The PDF data itself is
        // still served same-origin from `/api/gates/*.pdf` — only the worker
        // JS is loaded cross-origin.
        pdfjs.GlobalWorkerOptions.workerSrc =
          `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;
        const pdf = await pdfjs.getDocument({ data: await fetchPdfBytes(src) }).promise;
        if (cancelled) return;
        setPages(pdf.numPages);
        // Render each page into a canvas, into the container in order.
        const container = containerRef.current;
        if (!container) return;
        container.innerHTML = "";
        pagesRef.current = [];
        const targetWidth = Math.min(container.clientWidth - 24, 900);
        for (let n = 1; n <= pdf.numPages; n += 1) {
          if (cancelled) return;
          const page = await pdf.getPage(n);
          const viewport = page.getViewport({ scale: 1 });
          const scale = targetWidth / viewport.width;
          const scaled = page.getViewport({ scale });
          const canvas = document.createElement("canvas");
          canvas.width = scaled.width;
          canvas.height = scaled.height;
          canvas.className = "block mx-auto mb-4 shadow-md rounded";
          canvas.setAttribute("data-page", String(n));
          canvas.setAttribute("data-testid", `pdf-page-${n}`);
          container.appendChild(canvas);
          pagesRef.current.push(canvas);
          const ctx = canvas.getContext("2d");
          await page.render({ canvasContext: ctx, viewport: scaled }).promise;
        }
        loadingRef.current = false;
        setLoading(false);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("PDFViewer load failed", err);
        if (!cancelled) {
          setError(err?.message || "Could not load PDF");
          loadingRef.current = false;
          setLoading(false);
        }
      }
    }
    render();
    return () => { cancelled = true; };
  }, [src]);

  // ─── Scroll progress + completion detection ───────────────────────────
  const handleScroll = useCallback(() => {
    // While pages are still being rendered the scrollHeight is only partial —
    // evaluating progress now would let readers "finish" a half-rendered
    // document (real bypass found in testing). Wait for the full render.
    if (loadingRef.current) return;
    const el = containerRef.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    if (max <= 0) {
      if (!onReadEndCalledRef.current) {
        onReadEndCalledRef.current = true;
        onReadEnd?.();
      }
      onProgress?.(100);
      return;
    }
    const pct = Math.min(100, Math.round((el.scrollTop / max) * 100));
    onProgress?.(pct);
    // Fire onReadEnd once when the bottom is reached (within 5 %).
    if (pct >= 95 && !onReadEndCalledRef.current) {
      onReadEndCalledRef.current = true;
      onReadEnd?.();
    }
  }, [onReadEnd, onProgress]);

  // Single-page PDFs (or anything shorter than the viewport) can never be
  // "scrolled to the bottom" because there's no scroll. Fire onReadEnd
  // automatically once rendering finishes so the gate doesn't deadlock.
  useEffect(() => {
    if (loading || error || pages === 0) return;
    const el = containerRef.current;
    if (!el) return;
    // Allow a frame for layout to settle, then check.
    const id = requestAnimationFrame(() => handleScroll());
    return () => cancelAnimationFrame(id);
  }, [loading, error, pages, handleScroll]);

  return (
    <div className="relative h-full flex flex-col">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10" data-testid="pdf-viewer-loading">
          <Loader2 className="animate-spin text-[#E53935]" size={36} />
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-rose-50 z-10 p-4" data-testid="pdf-viewer-error">
          <div className="text-rose-900 text-sm text-center max-w-md">
            <div className="font-semibold mb-1">Couldn&rsquo;t load the document</div>
            <div className="text-rose-700">{error}</div>
          </div>
        </div>
      )}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        aria-label={ariaLabel}
        data-testid="pdf-viewer-scroll"
        className="flex-1 overflow-y-auto bg-slate-100 px-3 py-4"
      />
    </div>
  );
}

export default PDFViewer;
